/**
 * lib/cards/konva-renderers.js — Konva element primitives.
 *
 * Chunk 2 (Apr 24 2026): consume the same layout JSON that
 * render-layout.js feeds jsPDF. Each primitive returns Konva nodes
 * suitable for adding to a Konva Layer.
 *
 * Unit handling:
 *   Layouts are in mm. Konva Stage is in px. Every primitive here
 *   takes a `scale` (px per mm) and converts.
 *
 * Font handling:
 *   jsPDF uses pt. Konva uses px. 1pt ≈ 0.3528mm. So at scale px/mm,
 *   ptToPx(pt, scale) = pt * 0.3528 * scale.
 *
 * Baseline handling:
 *   jsPDF text is baseline-anchored when props.baselineAnchor=true
 *   (see template-*.js + layout-renderers.js). Konva Text nodes are
 *   top-anchored. To align visually we position the Konva Text with
 *   y = baselineY - capHeight, where capHeight ≈ 0.7 * fontSize(pt)
 *   converted to mm.
 */

import { Rect, Text, Line, Image as KonvaImage, Path, Group } from 'react-konva';
import { createElement } from 'react';

/* ─────────────────────────────────────────────────────────────── */
/*  Unit + colour helpers                                           */
/* ─────────────────────────────────────────────────────────────── */

export function ptToPx(pt, scale) {
  return pt * 0.3528 * scale;
}

export function capHeightMm(fontSizePt) {
  return fontSizePt * 0.7 * 0.3528;
}

function hexToRgb(hex) {
  const s = (hex || '').replace('#', '');
  if (s.length !== 6) return { r: 14, g: 26, b: 21 };
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const h = (v) => v.toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

function contrastingRgb(bg) {
  const norm = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * norm(bg.r) + 0.7152 * norm(bg.g) + 0.0722 * norm(bg.b);
  return L < 0.5 ? { r: 255, g: 255, b: 255 } : { r: 20, g: 30, b: 28 };
}

function resolveColorProp(value, ctx, options = {}) {
  if (!value) return { r: 14, g: 26, b: 21 };
  if (value === 'brand') return ctx.brandColor;
  if (value === 'auto-contrast') {
    const bg = options.backgroundColor || ctx.brandColor;
    return contrastingRgb(bg);
  }
  return hexToRgb(value);
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: static_rect                                          */
/* ─────────────────────────────────────────────────────────────── */

export function renderRectKonva(element, ctx, scale, keyPrefix = '') {
  const fill = resolveColorProp(element.props?.fill || 'brand', ctx);
  const radius = element.props?.cornerRadius || 0;
  const stroke = element.props?.stroke
    ? resolveColorProp(element.props.stroke, ctx)
    : null;

  return createElement(Rect, {
    key: `${keyPrefix}${element.id}`,
    x: element.x * scale,
    y: element.y * scale,
    width: element.w * scale,
    height: element.h * scale,
    fill: rgbToHex(fill),
    cornerRadius: radius * scale,
    stroke: stroke ? rgbToHex(stroke) : undefined,
    strokeWidth: stroke ? (element.props?.strokeWidth || 0.2) * scale : 0,
  });
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: static_divider                                       */
/* ─────────────────────────────────────────────────────────────── */

export function renderDividerKonva(element, ctx, scale, keyPrefix = '') {
  const color = resolveColorProp(element.props?.color || 'brand', ctx);
  const thickness = element.props?.thickness || 0.3;
  const orientation = element.props?.orientation || 'horizontal';

  let points;
  if (orientation === 'horizontal') {
    const y = (element.y + element.h / 2) * scale;
    points = [element.x * scale, y, (element.x + element.w) * scale, y];
  } else {
    const x = (element.x + element.w / 2) * scale;
    points = [x, element.y * scale, x, (element.y + element.h) * scale];
  }

  return createElement(Line, {
    key: `${keyPrefix}${element.id}`,
    points,
    stroke: rgbToHex(color),
    strokeWidth: thickness * scale,
  });
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: text                                                 */
/*                                                                  */
/*  Konva Text is top-anchored. To match jsPDF baseline placement   */
/*  we subtract capHeight from the baselineY.                       */
/*                                                                  */
/*  charSpace (jsPDF, mm) → letterSpacing (Konva, px):              */
/*    letterSpacingPx = charSpace_mm * scale                        */
/* ─────────────────────────────────────────────────────────────── */

export function renderTextKonva(element, content, ctx, scale, keyPrefix = '') {
  if (!content?.text) return null;

  const props = element.props || {};
  const fontFamily = fontFamilyCss(props.fontFamily || 'helvetica');
  const fontWeight = props.fontWeight || 'normal';
  const fontSize = props.fontSize || 10;
  const color = resolveColorProp(props.color, ctx);
  const align = props.align || 'left';
  const charSpaceMm = props.charSpace || 0;
  const uppercase = props.uppercase === true;
  const truncate = props.truncate !== false;

  let text = content.text;
  if (uppercase) text = text.toUpperCase();

  const fontSizePx = ptToPx(fontSize, scale);
  const capHPx = capHeightMm(fontSize) * scale;

  // Vertical placement
  let y;
  if (props.baselineAnchor === true) {
    // element.y is the baseline — subtract capHeight to get Konva top
    y = element.y * scale - capHPx;
  } else {
    const verticalAlign = props.verticalAlign || 'top';
    if (verticalAlign === 'center') {
      y = (element.y + element.h / 2) * scale - capHPx / 2 - (capHPx / 2);
    } else if (verticalAlign === 'bottom') {
      y = (element.y + element.h) * scale - capHPx;
    } else {
      y = element.y * scale;
    }
  }

  const fontStyle = fontWeight === 'bold' ? 'bold' : 'normal';

  const commonProps = {
    key: `${keyPrefix}${element.id}`,
    x: element.x * scale,
    y,
    width: element.w * scale,
    text,
    fontSize: fontSizePx,
    fontFamily,
    fontStyle,
    fill: rgbToHex(color),
    letterSpacing: charSpaceMm * scale,
    align,
    // truncate → ellipsis; off → wrap not allowed in our layouts anyway
    wrap: truncate ? 'none' : 'word',
    ellipsis: truncate,
    // Konva vertical alignment is within the height box — we already
    // computed the top y to place baseline correctly, so height can
    // be generous
    height: Math.max(element.h * scale, fontSizePx * 1.4),
  };

  return createElement(Text, commonProps);
}

function fontFamilyCss(family) {
  // Map jsPDF standard fonts to CSS fallbacks that render close
  switch (family) {
    case 'times':   return 'Times, "Times New Roman", serif';
    case 'courier': return '"Courier New", monospace';
    case 'helvetica':
    default:        return 'Helvetica, Arial, sans-serif';
  }
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: image — photo, logo, banner, static_image            */
/*                                                                  */
/*  Konva Image takes an HTMLImageElement. Caller resolves images   */
/*  async before render and passes them in contentMap.              */
/* ─────────────────────────────────────────────────────────────── */

export function renderImageKonva(element, content, ctx, scale, keyPrefix = '') {
  if (!content?.image) return null;

  const props = element.props || {};
  const fit = props.fit || 'cover';

  const naturalW = content.image.naturalWidth || content.width || element.w;
  const naturalH = content.image.naturalHeight || content.height || element.h;

  let w, h, x, y;
  if (fit === 'contain' && naturalW && naturalH) {
    const ratio = Math.min(element.w / naturalW, element.h / naturalH);
    w = naturalW * ratio;
    h = naturalH * ratio;
    // Top-left anchor (matches jsPDF template-minimal / template-premium)
    x = element.x;
    y = element.y;
  } else {
    // cover: caller has pre-cropped OR we accept aspect distortion
    w = element.w;
    h = element.h;
    x = element.x;
    y = element.y;
  }

  return createElement(KonvaImage, {
    key: `${keyPrefix}${element.id}`,
    x: x * scale,
    y: y * scale,
    width: w * scale,
    height: h * scale,
    image: content.image,
  });
}

/* ─────────────────────────────────────────────────────────────── */
/*  Primitive: QR                                                   */
/* ─────────────────────────────────────────────────────────────── */

export function renderQRKonva(element, content, ctx, scale, keyPrefix = '') {
  if (!content?.image) return null;

  const minSize = 18;
  const renderW = Math.max(element.w, minSize);
  const renderH = Math.max(element.h, minSize);

  return createElement(KonvaImage, {
    key: `${keyPrefix}${element.id}`,
    x: element.x * scale,
    y: element.y * scale,
    width: renderW * scale,
    height: renderH * scale,
    image: content.image,
    // Avoid smoothing the QR — keeps modules crisp on Retina
    imageSmoothingEnabled: false,
  });
}

/* ─────────────────────────────────────────────────────────────── */
/*  Photo placeholder — equivalent of drawPhotoPlaceholder          */
/*  Only invoked when contentMap.el_staff_photo has no image.       */
/* ─────────────────────────────────────────────────────────────── */

export function renderPhotoPlaceholder(placeholder, initials, scale, keyPrefix = '') {
  const bg = '#F4E9D6';
  const fg = '#6B7A70';
  const fontSize = placeholder.fontSize || 18;

  const rect = createElement(Rect, {
    key: `${keyPrefix}ph-rect`,
    x: placeholder.x * scale,
    y: placeholder.y * scale,
    width: placeholder.w * scale,
    height: placeholder.h * scale,
    fill: bg,
  });

  const fontSizePx = ptToPx(fontSize, scale);
  const text = createElement(Text, {
    key: `${keyPrefix}ph-text`,
    x: placeholder.x * scale,
    y: placeholder.y * scale,
    width: placeholder.w * scale,
    height: placeholder.h * scale,
    text: initials || '?',
    fontSize: fontSizePx,
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontStyle: 'bold',
    fill: fg,
    align: 'center',
    verticalAlign: 'middle',
  });

  return createElement(Group, { key: `${keyPrefix}ph-group` }, rect, text);
}

/* ─────────────────────────────────────────────────────────────── */
/*  Wave band (Minimal bottom) — parallel to defaults/wave.js       */
/*                                                                  */
/*  Three sine-wave paths + solid brand footer strip. Matches the   */
/*  HTML twin's wavePath() logic.                                   */
/* ─────────────────────────────────────────────────────────────── */

function buildWavePath(baselineYmm, cardWidthMm, amplitudeMm, wavelengthMm, endYmm) {
  // Generate SVG path string for a sine wave from (0, baselineY) to
  // (width, baselineY) with the given amplitude, then close down to
  // endYmm. Points are in mm — scaling happens at Konva render time.
  const cols = 50;
  const pts = [];
  for (let i = 0; i <= cols; i++) {
    const x = (i / cols) * cardWidthMm;
    const y = baselineYmm + Math.sin((x / wavelengthMm) * Math.PI * 2) * amplitudeMm;
    pts.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(3)},${y.toFixed(3)}`);
  }
  pts.push(`L${cardWidthMm},${endYmm} L0,${endYmm} Z`);
  return pts.join(' ');
}

export function renderWaveBandKonva(brandColor, waveTopYmm, cardWidthMm, cardHeightMm, scale) {
  const bandHeightMm = cardHeightMm - waveTopYmm; // e.g. 54 - 49 = 5mm
  const baselineA = waveTopYmm + bandHeightMm * 0.3;
  const baselineB = waveTopYmm + bandHeightMm * 0.55;
  const baselineC = waveTopYmm + bandHeightMm * 0.8;

  // Footer strip (solid brand colour, bottom ~4mm)
  const footerTopMm = cardHeightMm - 4;

  const brandHex = rgbToHex(brandColor);

  const waveA = createElement(Path, {
    key: 'wave-a',
    data: buildWavePath(baselineA, cardWidthMm, 0.9, 22, footerTopMm),
    fill: brandHex,
    opacity: 0.15,
    scaleX: scale,
    scaleY: scale,
  });
  const waveB = createElement(Path, {
    key: 'wave-b',
    data: buildWavePath(baselineB, cardWidthMm, 1.0, 20, footerTopMm),
    fill: brandHex,
    opacity: 0.25,
    scaleX: scale,
    scaleY: scale,
  });
  const waveC = createElement(Path, {
    key: 'wave-c',
    data: buildWavePath(baselineC, cardWidthMm, 1.1, 18, footerTopMm),
    fill: brandHex,
    opacity: 0.45,
    scaleX: scale,
    scaleY: scale,
  });
  const footer = createElement(Rect, {
    key: 'wave-footer',
    x: 0,
    y: footerTopMm * scale,
    width: cardWidthMm * scale,
    height: (cardHeightMm - footerTopMm) * scale,
    fill: brandHex,
  });

  return createElement(Group, { key: 'wave-band' }, waveA, waveB, waveC, footer);
}

/* ─────────────────────────────────────────────────────────────── */
/*  Mountain range (Premium default banner) — parallel to           */
/*  defaults/mountains.js. Three-layer silhouette.                  */
/* ─────────────────────────────────────────────────────────────── */

function buildMountainPath(points, cardWidthMm, endYmm) {
  // points: array of [xMm, yMm]. Close path down to endY.
  const parts = points.map(([x, y], i) =>
    `${i === 0 ? 'M' : 'L'}${x.toFixed(3)},${y.toFixed(3)}`
  );
  parts.push(`L${cardWidthMm},${endYmm} L0,${endYmm} Z`);
  return parts.join(' ');
}

export function renderMountainsKonva(brandColor, bannerTopMm, cardWidthMm, cardHeightMm, scale) {
  // Scale the HTML twin's mountain silhouettes (designed in 0-856 × 0-90
  // pixel space) back into mm space between bannerTopMm and cardHeight.
  const bandTop = bannerTopMm;
  const bandBottom = cardHeightMm - 4; // leave room for footer strip
  const bandHeight = bandBottom - bandTop;

  // Convert a "0-90" y value from the HTML twin into mm
  const y = (v) => bandTop + (v / 90) * bandHeight;
  // Convert a "0-856" x value into mm
  const x = (v) => (v / 856) * cardWidthMm;

  const far = [
    [x(0), y(50)], [x(50), y(30)], [x(110), y(45)], [x(180), y(20)],
    [x(250), y(40)], [x(330), y(25)], [x(400), y(35)], [x(490), y(20)],
    [x(560), y(42)], [x(640), y(28)], [x(720), y(40)], [x(800), y(30)],
    [x(856), y(38)],
  ];
  const mid = [
    [x(0), y(60)], [x(70), y(30)], [x(140), y(50)], [x(210), y(15)],
    [x(290), y(45)], [x(370), y(20)], [x(450), y(40)], [x(530), y(18)],
    [x(610), y(42)], [x(690), y(22)], [x(770), y(38)], [x(850), y(30)],
    [x(856), y(35)],
  ];
  const near = [
    [x(0), y(70)], [x(60), y(40)], [x(130), y(55)], [x(200), y(25)],
    [x(280), y(55)], [x(370), y(35)], [x(450), y(50)], [x(530), y(30)],
    [x(610), y(50)], [x(690), y(35)], [x(770), y(45)], [x(840), y(38)],
    [x(856), y(42)],
  ];

  const brandHex = rgbToHex(brandColor);

  const layerFar = createElement(Path, {
    key: 'mt-far',
    data: buildMountainPath(far, cardWidthMm, bandBottom),
    fill: brandHex,
    opacity: 0.25,
    scaleX: scale,
    scaleY: scale,
  });
  const layerMid = createElement(Path, {
    key: 'mt-mid',
    data: buildMountainPath(mid, cardWidthMm, bandBottom),
    fill: brandHex,
    opacity: 0.45,
    scaleX: scale,
    scaleY: scale,
  });
  const layerNear = createElement(Path, {
    key: 'mt-near',
    data: buildMountainPath(near, cardWidthMm, bandBottom),
    fill: brandHex,
    opacity: 0.65,
    scaleX: scale,
    scaleY: scale,
  });
  const footer = createElement(Rect, {
    key: 'mt-footer',
    x: 0,
    y: bandBottom * scale,
    width: cardWidthMm * scale,
    height: (cardHeightMm - bandBottom) * scale,
    fill: brandHex,
  });

  return createElement(Group, { key: 'mountain-band' }, layerFar, layerMid, layerNear, footer);
}

/* ─────────────────────────────────────────────────────────────── */
/*  Banner image (uploaded) — Premium only                          */
/*                                                                  */
/*  Renders the cropped banner image + brand-colour footer strip +  */
/*  optional opacity overlay.                                       */
/* ─────────────────────────────────────────────────────────────── */

export function renderBannerImageKonva(
  bannerImage,
  bannerOpacity,
  banner,
  brandColor,
  cardWidthMm,
  cardHeightMm,
  scale,
) {
  const brandHex = rgbToHex(brandColor);
  const children = [];

  // The banner image — scaled to the banner slot (cover-fit; caller
  // may have pre-cropped, but for preview we just let Konva scale it)
  if (bannerImage) {
    children.push(createElement(KonvaImage, {
      key: 'banner-image',
      x: 0,
      y: banner.topY * scale,
      width: cardWidthMm * scale,
      height: banner.height * scale,
      image: bannerImage,
    }));

    // Opacity overlay (white, alpha = 1 - opacity)
    if (bannerOpacity < 1.0) {
      children.push(createElement(Rect, {
        key: 'banner-overlay',
        x: 0,
        y: banner.topY * scale,
        width: cardWidthMm * scale,
        height: banner.height * scale,
        fill: '#FFFFFF',
        opacity: 1.0 - bannerOpacity,
      }));
    }
  }

  // Footer strip
  const footerTopMm = banner.topY + banner.height;
  children.push(createElement(Rect, {
    key: 'banner-footer',
    x: 0,
    y: footerTopMm * scale,
    width: cardWidthMm * scale,
    height: (cardHeightMm - footerTopMm) * scale,
    fill: brandHex,
  }));

  return createElement(Group, { key: 'banner-group' }, ...children);
}

/* ─────────────────────────────────────────────────────────────── */
/*  Utility: sort elements by z-order                               */
/* ─────────────────────────────────────────────────────────────── */

export function sortByZ(elements) {
  return [...elements].sort((a, b) => (a.z || 0) - (b.z || 0));
}

/* ─────────────────────────────────────────────────────────────── */
/*  Dispatch: element → Konva node                                  */
/* ─────────────────────────────────────────────────────────────── */

export function dispatchElementKonva(element, content, ctx, scale, keyPrefix = '') {
  switch (element.type) {
    case 'static_rect':    return renderRectKonva(element, ctx, scale, keyPrefix);
    case 'static_divider': return renderDividerKonva(element, ctx, scale, keyPrefix);
    case 'static_text':
      return renderTextKonva(
        element,
        { text: content?.text ?? element.props?.text ?? '' },
        ctx,
        scale,
        keyPrefix,
      );
    case 'dynamic_name':
    case 'dynamic_role':
    case 'dynamic_footer_text':
      return renderTextKonva(element, content || { text: '' }, ctx, scale, keyPrefix);
    case 'dynamic_photo':
    case 'dynamic_logo':
    case 'dynamic_banner':
    case 'static_image':
      return renderImageKonva(element, content, ctx, scale, keyPrefix);
    case 'dynamic_qr':
      return renderQRKonva(element, content, ctx, scale, keyPrefix);
    default:
      console.warn(`[konva-renderers] unknown element type: ${element.type}`);
      return null;
  }
}
